import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import { buildHideCssClasses } from '@/helpers';
import { BreakpointTypes } from '@/types';
import { AssetWrapper } from '../AssetWrapper';
import { Button } from '../Button';
import { VideoThumbnail } from '../VideoThumbnail';
import styles from './Youtube.module.scss';

interface YoutubeProps {
	uniqueVideoId: string;
	thumbnailUrl: string;
	controls?: boolean;
	autoPlay?: boolean;
	caption?: string;
	title?: string;
	aspectRatio?: string;
	hideOnBreakpoint?: BreakpointTypes[];
	isSmall?: boolean;
}

export interface YoutubeHandle {
	pauseVideo: () => void;
}

export const Youtube = forwardRef<YoutubeHandle, YoutubeProps>(
	(
		{
			uniqueVideoId,
			thumbnailUrl,
			controls = false,
			autoPlay = false,
			caption,
			title,
			aspectRatio,
			hideOnBreakpoint,
			isSmall,
		},
		ref,
	) => {
		const [renderVideo, setRenderVideo] = React.useState(autoPlay);
		const iframeRef = useRef<HTMLIFrameElement>(null);

		const providerSrc = 'https://www.youtube.com/embed/';
		const extraParams = `&mute=1&controls=${controls ? 1 : 0}&rel=0&cc_load_policy=1&loop=1&playlist=${uniqueVideoId}&enablejsapi=1`;
		const videoSrc = `${providerSrc}${uniqueVideoId}?autoplay=1${extraParams}`;

		const handleClick = (): void => {
			setRenderVideo(true);
		};

		useImperativeHandle(ref, () => ({
			pauseVideo: () => {
				if (iframeRef.current) {
					iframeRef.current.contentWindow?.postMessage(
						'{"event":"command","func":"pauseVideo","args":""}',
						'*',
					);
				}
			},
		}));

		return (
			<div className={classNames(styles.wrapper, hideOnBreakpoint && buildHideCssClasses(hideOnBreakpoint))}>
				{renderVideo ? (
					<AssetWrapper fixedRatio aspectRatio={aspectRatio}>
						<iframe
							ref={iframeRef}
							src={videoSrc}
							title={title || ''}
							width="100%"
							height="100%"
							allow="autoplay; encrypted-media"
							allowFullScreen
						/>
					</AssetWrapper>
				) : (
					<Button className={styles.button} hasNoStyles onClick={handleClick}>
						<VideoThumbnail
							imageUrl={thumbnailUrl}
							thumbnailAlternativeText={title || ''}
							aspectRatio={aspectRatio}
							isSmall={isSmall}
						/>
					</Button>
				)}
				{caption && <div dangerouslySetInnerHTML={{ __html: caption }} />}
			</div>
		);
	},
);
Youtube.displayName = 'Youtube';
